import React, { useState } from 'react'
import { Formik } from 'formik'
import { useServiceContext } from '../../root'
import { toast } from '../../services/toast'

interface ModalImportProps {
  importType: string
  show: boolean
  onClose: () => void
}

const SUPPORTED_FORMATS = ['application/json']

export function ModalImport({ importType, show, onClose }: ModalImportProps) {
  const context = useServiceContext()
  const { translate } = context.service.filter
  const [file, setFile] = useState<File | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const doSave = async () => {
    if (!file) return
    setIsSubmitting(true)
    try {
      await context.api.settings.importSettingsData(
        file,
        [importType],
        (data) => {
          toast.success(translate('SETTINGS_IMPORT_SUCCESS'))
          onClose()
        },
        (error) => {
          toast.error(translate('SETTINGS_IMPORT_FAIL'))
        }
      )
    } catch (error) {
      toast.error(translate('SETTINGS_IMPORT_FAIL'))
    } finally {
      setIsSubmitting(false)
    }
  }

  if (!show) return null

  return (
    <div className="theme-sandman">
      <div className="modal-backdrop fade in" aria-hidden="true" />
      <div className="modal fade in" style={{ display: 'block' }} tabIndex={-1} role="dialog">
        <div className="modal-dialog modal-md" onClick={(e) => e.stopPropagation()}>
          <div className="modal-content">
            <div className="modal-header">
              <button type="button" className="close" onClick={onClose} aria-label="Close">
                <span aria-hidden="true">&times;</span>
              </button>
              <h4 className="modal-title">{context.service.filter.translate('SETTINGS_IMPORT_MODAL_TITLE')}</h4>
            </div>
            <Formik
              initialValues={{ file: '' }}
              validate={(values) => {
                const errors: { file?: string } = {}
                if (!file || !file.name) {
                  errors.file = 'SETTINGS_IMPORT_UPLOAD'
                } else if (file && !SUPPORTED_FORMATS.includes(file.type)) {
                  errors.file = 'SETTINGS_IMPORT_UPLOAD_JSON'
                }
                return errors
              }}
              validateOnChange={true}
              onSubmit={doSave}
            >
              {({ handleSubmit, errors, touched, setFieldValue }) => (
                <form onSubmit={handleSubmit}>
                  <div className="modal-body">
                    <p>{context.service.filter.translate('SETTINGS_IMPORT_VALID_JSON')}</p>
                    <label
                      style={{ display: 'inline' }}
                      htmlFor="import-file-input"
                      className="btn btn-md btn-upload btn-chita btn-second text-transform-none"
                    >
                      {file?.name || context.service.filter.translate('BROWSER_BROWSE_FILE')}
                    </label>
                    <input
                      type="file"
                      name="file"
                      id="import-file-input"
                      style={{ display: 'none' }}
                      onChange={(event) => {
                        const selectedFile = event.currentTarget.files?.[0]
                        if (selectedFile) {
                          setFile(selectedFile)
                          setFieldValue('file', selectedFile)
                        }
                      }}
                      className="form-control"
                    />
                    {errors.file && touched.file ? (
                      <div className="text-danger">{context.service.filter.translate(errors.file)}</div>
                    ) : null}
                  </div>
                  <div className="modal-footer">
                    <button
                      type="button"
                      className="btn btn-link"
                      onClick={onClose}
                      data-action="cancel"
                      disabled={isSubmitting}
                    >
                      {context.service.filter.translate('CANCEL')}
                    </button>
                    <button type="submit" className="btn btn-primary" data-action="ok" disabled={isSubmitting}>
                      {isSubmitting
                        ? context.service.filter.translate('SAVING')
                        : context.service.filter.translate('OK')}
                    </button>
                  </div>
                </form>
              )}
            </Formik>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ModalImport
